import React from "react";
import QRCode from "react-qr-code";
import { fmtDay, rupees } from "../../rates/rateModel";
import { fmtDateTime } from "../money";
import type { PrintRow, PrintView } from "../types";
import "./cash-sheet-print.scss";

/**
 * Geometry of one printed page (A4 portrait, millimetres). Every page has the same header and the same
 * 15 row slots: a short last page prints its empty slots struck through, so a row's position never
 * depends on how many people are on the page. The sheet check pans a photo to a row band with
 * `rowBand(rowOnPage)` (V1_SPEC D31, 11.6 L3). Keep these numbers in step with cash-sheet-print.scss.
 */
export const PRINT_GEOMETRY = {
  pageWidthMm: 210,
  pageHeightMm: 297,
  /** Corner marks (L-shapes, 8 mm arms) sit this far in from each edge. */
  cornerInsetMm: 6,
  cornerArmMm: 8,
  /** Top of the first row (below the header and the column titles). */
  bodyTopMm: 67,
  rowHeightMm: 11.5,
  rowsPerPage: 15,
  /** Fractions of the page height, for the photo band: top = headerFraction + (row − 0.5) / rowsPerPage × bodyFraction. */
  headerFraction: 67 / 297,
  bodyFraction: (11.5 * 15) / 297,
} as const;

/** Centre and height of a row's band as fractions of the page height (rowOnPage is 1-based). */
export const rowBand = (rowOnPage: number, rowsPerPage: number = PRINT_GEOMETRY.rowsPerPage) => {
  const g = PRINT_GEOMETRY;
  const body = g.bodyFraction * (rowsPerPage / g.rowsPerPage);
  return { center: g.headerFraction + ((rowOnPage - 0.5) / rowsPerPage) * body, height: body / rowsPerPage };
};

/** The rows of each page, in page order (pages with no rows still appear, so page n is always index n−1). */
export const rowsByPage = (print: Pick<PrintView, "pages" | "rows">): PrintRow[][] => {
  const pages: PrintRow[][] = Array.from({ length: Math.max(1, print.pages || 1) }, () => []);
  (print.rows || []).forEach((r) => {
    const i = Math.min(Math.max(1, r.page || 1), pages.length) - 1;
    pages[i].push(r);
  });
  pages.forEach((p) => p.sort((a, b) => a.rowIndex - b.rowIndex));
  return pages;
};

/** QR text for a page, as the server built it (`HKPCB1|printId|sheetNo|revision|page|pages|rowsOnPage`). */
export const qrFor = (print: Pick<PrintView, "qr">, page: number): string => print.qr?.find((q) => q.page === page)?.text || "";

const Corners = () => (
  <>
    <span className="cbp-corner is-tl" aria-hidden="true" />
    <span className="cbp-corner is-tr" aria-hidden="true" />
    <span className="cbp-corner is-bl" aria-hidden="true" />
    <span className="cbp-corner is-br" aria-hidden="true" />
  </>
);

type Props = {
  print: PrintView;
  /** Opened again later (not a fresh print): every page carries a diagonal "कॉपी · Copy" mark. */
  copy?: boolean;
};

/**
 * The frozen, numbered pay sheet people sign (V1_SPEC 11.4 P0, D9, D10; UX_COPY frame 1). Blank "दिया"
 * means not paid. Per-page total on every page; the grand TOTAL, row count, payer line, instructions and
 * the DPDP notice on the last page.
 */
const CashSheetPrint = ({ print, copy = false }: Props) => {
  const pages = rowsByPage(print);
  const slots = print.rowsPerPage || PRINT_GEOMETRY.rowsPerPage;
  const studio = print.orgName || "Karkhana";
  const last = pages.length;
  return (
    <div className="cbp">
      {pages.map((rows, i) => {
        const page = i + 1;
        const empty = Math.max(0, slots - rows.length);
        return (
          <section key={page} className="cbp-page" aria-label={`पन्ना ${page} / ${last}`} data-page={page}>
            <Corners />
            {copy ? (
              <div className="cbp-copy" aria-hidden="true">
                कॉपी · Copy
              </div>
            ) : null}
            <header className="cbp-head">
              <div className="cbp-head__main">
                <h1 className="cbp-title">
                  भुगतान पत्रक {print.sheetNo} · Pay sheet
                </h1>
                <div className="cbp-studio">{studio}</div>
                <div className="cbp-meta">
                  दिन Days: {fmtDay(print.from)} – {fmtDay(print.to)} · प्रिंट {print.revision} Print {print.revision}
                </div>
                <div className="cbp-meta">
                  छपा Printed {fmtDateTime(print.printedAt)} · {print.printedBy?.name || ""}
                  {print.holder?.name ? ` · भुगतान करने वाले ${print.holder.name}` : ""}
                </div>
                {print.revision > 1 ? (
                  <div className="cbp-reprint">
                    प्रिंट {print.revision} · प्रिंट {print.revision - 1} की जगह। सिर्फ़ इसी से भुगतान करें। · Print {print.revision} replaces Print {print.revision - 1}. Pay only from this one.
                  </div>
                ) : null}
                <div className="cbp-due">
                  कुल देय Total due <strong>{rupees(print.totalNetDue)}</strong>
                  <span className="cbp-pageno">
                    पन्ना {page} / {last} · Page {page} of {last}
                  </span>
                </div>
              </div>
              <div className="cbp-qr" data-qr={qrFor(print, page)}>
                {qrFor(print, page) ? <QRCode value={qrFor(print, page)} size={256} viewBox="0 0 256 256" /> : null}
              </div>
            </header>
            <table className="cbp-table">
              <thead>
                <tr>
                  <th className="c-n">#</th>
                  <th className="c-name">
                    नाम <span>Name</span>
                  </th>
                  <th className="c-code">
                    कोड <span>Code</span>
                  </th>
                  <th className="c-due">
                    देय राशि <span>Net to pay</span>
                  </th>
                  <th className="c-paid">
                    दिया <span>Paid</span>
                  </th>
                  <th className="c-sign">
                    हस्ताक्षर / अँगूठा <span>Signature / thumb</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.rowIndex} className="cbp-row">
                    <td className="c-n">{r.rowIndex}</td>
                    <td className="c-name">{r.name}</td>
                    <td className="c-code">{r.code}</td>
                    <td className="c-due">{rupees(r.netDue)}</td>
                    <td className="c-paid">
                      <span className="cbp-paidbox">₹</span>
                    </td>
                    <td className="c-sign" />
                  </tr>
                ))}
                {Array.from({ length: empty }, (_, k) => (
                  <tr key={`empty-${k}`} className="cbp-row is-empty" aria-hidden="true">
                    <td colSpan={6} />
                  </tr>
                ))}
              </tbody>
            </table>
            <footer className="cbp-foot">
              <div className="cbp-pagetotal">
                इस पन्ने का कुल दिया <span>Paid on this page</span> ₹<span className="cbp-blank" />
              </div>
              {page === last ? (
                <>
                  <div className="cbp-total">
                    <span>
                      सब पन्नों का कुल दिया <strong>TOTAL PAID</strong>
                    </span>
                    <span className="cbp-totalbox">₹</span>
                    <span className="cbp-count">
                      {print.rowCount} लोग · {print.rowCount} people
                    </span>
                  </div>
                  <div className="cbp-payer">
                    भुगतान किया Paid by: <span className="cbp-blank is-wide" /> (नाम, हस्ताक्षर Name, signature) · शुरू में नकद Cash at start ₹
                    <span className="cbp-blank" />
                  </div>
                  <div className="cbp-strip">
                    हर व्यक्ति को भुगतान करें, "दिया" में रकम लिखें, हस्ताक्षर या अँगूठा लगवाएँ। जो नहीं आया, उसका दिया ख़ाली छोड़ें। शाम को इस पत्रक की फ़ोटो भेजें।
                  </div>
                  <div className="cbp-dpdp">
                    आपका नाम, कोड, दी गई रकम और हस्ताक्षर या अँगूठा {studio} मज़दूरी रिकॉर्ड के रूप में 8 साल रखेगा। · Your name, code, amount paid and signature or
                    thumbprint are kept by {studio} as wage records for 8 years.
                  </div>
                </>
              ) : null}
            </footer>
          </section>
        );
      })}
    </div>
  );
};

export default CashSheetPrint;
